import Phaser from "phaser";
import type { TabViewContract, TabContentRect } from "@yjsgames/phaser-ui/tab-registry";
import {
  NINE_SLICE_BUTTON_EVENT,
  SCROLLABLE_LIST_EVENT,
  SEGMENTED_TAB_BAR_EVENT,
  NineSliceButton,
  ScrollableList,
  SegmentedTabBar,
  type TabBarStyleToken,
} from "@yjsgames/phaser-ui";
import type { Rect } from "@yjsgames/phaser-ui/panel-content";
import { SingleSelection } from "@yjsgames/single-select";
import { SPACESHIP_LAUNCHER_CODEX_PREFIX, type CodexEntry, type SynergyCodexRow } from "../data/codex.js";
import {
  FONT_FAMILY,
  PRIMARY_BUTTON_STYLE,
  hexToNum,
  LOCK_ICON_KEY,
  LOCKED_COLOR,
  RARITY_COLOR,
  TYPO,
  setButtonEnabled,
} from "./uiTokens.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { computeCodexLayout } from "./codexLayout.js";
import { isPieceUnlocked } from "../data/modulePieces.js";
import { clampWrappedLines } from "./clampLines.js";
import { t, getTextDirection } from "../i18n/i18n.js";
import type { TextDirection } from "@yjsgames/i18n";
import { TipCarouselView } from "./TipCarouselView.js";
import {
  ScrollInlineBannerSlot,
  shouldMountCrazyGamesInlineBannerAtRuntime,
} from "./inlineBannerPlaceholder.js";
import { moduleCodexIconKey } from "./modulePresentationIcon.js";
import { REWARD_ICON_KEY } from "./rewardIcons.js";
import { MultiplierSelector } from "./MultiplierSelector.js";
import type { Multiplier } from "../systems/multiBuy.js";
import { drawSynergyIcon } from "./synergyIcon.js";

type CodexSubTabId = "modules" | "synergy";
type CodexModuleFilterId = "all" | "base" | "ship";
type CodexTextRole = "moduleName" | "moduleDesc" | "lockBadge" | "synergyName" | "synergyDetail" | "synergyCost";

const SUB_BAR_HEIGHT = 30;
const FILTER_BAR_HEIGHT = 26;
const SUB_TAB_BAR_STYLE: TabBarStyleToken = {
  activeTextureKey: "tab_button_active_9slice",
  inactiveTextureKey: "tab_button_inactive_9slice",
  border: { left: 10, top: 10, right: 10, bottom: 10 },
  labelStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textSecondary },
  labelActiveStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textBright },
  inactiveAlpha: 0.65,
  activeLabelScale: 1.04,
};

// 시너지 섹션 메트릭 — scroll 로컬좌표.
const SYNERGY_ROW_H = 86; // 시너지 행 1개 높이(이름 + 조건/현재효과/다음효과 + 강화 버튼)
const SYNERGY_ROW_GAP = 6; // 행 간 간격
const SYNERGY_ROW_PAD = 10; // 행 좌우/내부 패딩
const SYNERGY_SIDE_INSET = 4; // 행 좌측 시작 x
const SYNERGY_SCROLLBAR_INSET = 20; // 스크롤바 거터(행 폭에서 차감)
const SYNERGY_BUTTON_W = 74; // 행 우측 강화 버튼 폭
const SYNERGY_BUTTON_H = 30; // 행 우측 강화 버튼 높이
const SYNERGY_TEXT_GAP = 8;
const SYNERGY_BADGE_SIZE = 26;
const SYNERGY_BADGE_GAP = 8;
const SYNERGY_MULT_H = 26;
const SYNERGY_MULT_GAP = 4;

const CODEX_TEXT_FIT: Record<CodexTextRole, { preferred: number; min: number; maxLines: number }> = {
  moduleName: { preferred: 11, min: 10, maxLines: 2 },
  moduleDesc: { preferred: 11, min: 10, maxLines: 2 },
  lockBadge: { preferred: 11, min: 10, maxLines: 1 },
  synergyName: { preferred: 13, min: 12, maxLines: 1 },
  synergyDetail: { preferred: 12, min: 10, maxLines: 3 },
  synergyCost: { preferred: 12, min: 11, maxLines: 2 },
};

function iconTab(activeIconKey: string): { iconKey: string; activeIconKey: string; inactiveIconKey: string } {
  return { iconKey: activeIconKey, activeIconKey, inactiveIconKey: `${activeIconKey}_inactive` };
}

function synergyBuyLabel(row: SynergyCodexRow): string {
  const cost = t("synergyCodex.upgradeCost", { cost: row.stoneCost });
  return row.count > 1 ? `${cost} x${row.count}` : cost;
}

function synergyGradeColor(grade: string): string {
  if (grade === "advanced") return RARITY_COLOR.rare;
  if (grade === "rare") return RARITY_COLOR.epic;
  if (grade === "legendary") return RARITY_COLOR.legendary;
  return RARITY_COLOR.common;
}

interface CodexCard {
  readonly frame: Phaser.GameObjects.Rectangle;
  readonly name: Phaser.GameObjects.Text;
  readonly desc: Phaser.GameObjects.Text;
  readonly moduleIcon: Phaser.GameObjects.Image;
  readonly lockIcon: Phaser.GameObjects.Image;
  readonly lockText: Phaser.GameObjects.Text;
  readonly newDot: Phaser.GameObjects.Arc;
}

export interface CodexTabViewOptions {
  readonly onRowClick?: (definitionId: string) => void;
  readonly onLockedClick?: (chapter: number) => void;
  readonly onSynergyUpgrade?: (synergyId: string) => void;
  readonly onSynergyInfoClick?: (synergyId: string) => void;
  readonly onSynergyMultiplierChange?: (mult: Multiplier) => void;
  readonly onSynergyLockedClick?: (chapter: number) => void;
}

export class CodexTabView implements TabViewContract<readonly CodexEntry[]> {
  private readonly header: Phaser.GameObjects.Text;
  private readonly subBar: SegmentedTabBar<CodexSubTabId>;
  private readonly selection = new SingleSelection<CodexSubTabId>(["modules", "synergy"], "modules");
  private readonly filterBar: SegmentedTabBar<CodexModuleFilterId>;
  private readonly moduleFilter = new SingleSelection<CodexModuleFilterId>(["all", "base", "ship"], "all");
  private readonly tipView: TipCarouselView;
  private readonly synergyTipView: TipCarouselView;
  private readonly scrollList: ScrollableList;
  private readonly synergyScrollList: ScrollableList;
  private readonly synergyMultSelector: MultiplierSelector;
  private readonly bannerSlot: ScrollInlineBannerSlot;
  private readonly inlineBannerEnabled: boolean;
  private readonly cards: CodexCard[] = [];
  private lastEntries: readonly CodexEntry[] = [];
  private readonly synergyRows: {
    readonly frame: Phaser.GameObjects.Rectangle;
    readonly synergyIcon: Phaser.GameObjects.Graphics;
    readonly name: Phaser.GameObjects.Text;
    readonly detail: Phaser.GameObjects.Text;
    readonly upgradeButton: NineSliceButton;
  }[] = [];
  private lastSynergies: readonly SynergyCodexRow[] = [];
  private synergyUnlocked = true;
  private synergyUnlockChapter = 1;
  private content: Rect;
  private readonly poolCellPx: number;
  private poolMaxWidth: number | undefined;
  private readonly onRowClick?: (definitionId: string) => void;
  private readonly onLockedClick?: (chapter: number) => void;
  private readonly onSynergyUpgrade?: (synergyId: string) => void;
  private readonly onSynergyInfoClick?: (synergyId: string) => void;
  private readonly onSynergyMultiplierChange?: (mult: Multiplier) => void;
  private readonly onSynergyLockedClick?: (chapter: number) => void;
  private dir: TextDirection = getTextDirection();
  private visible = false;
  private readonly contentBottom: number;

  constructor(
    private readonly scene: Phaser.Scene,
    x: number,
    y: number,
    width: number,
    panelBottom: number,
    opts?: CodexTabViewOptions,
  ) {
    this.content = { x, y, width, height: 0 };
    this.contentBottom = panelBottom - SUB_BAR_HEIGHT;
    this.onRowClick = opts?.onRowClick;
    this.onLockedClick = opts?.onLockedClick;
    this.onSynergyUpgrade = opts?.onSynergyUpgrade;
    this.onSynergyInfoClick = opts?.onSynergyInfoClick;
    this.onSynergyMultiplierChange = opts?.onSynergyMultiplierChange;
    this.onSynergyLockedClick = opts?.onSynergyLockedClick;
    this.inlineBannerEnabled = shouldMountCrazyGamesInlineBannerAtRuntime();
    const L0 = computeCodexLayout(this.content, 0, this.contentBottom, this.dir, {
      inlineBannerEnabled: this.inlineBannerEnabled,
    });
    this.poolCellPx = L0.poolCellPx;
    this.header = scene.add
      .text(L0.header.x, L0.header.y, "", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.label,
        color: PALETTE.textSecondary,
      })
      .setOrigin(L0.textOriginX, 0)
      .setVisible(false)
      .setDepth(2);
    this.tipView = new TipCarouselView(scene, L0.tip.x, L0.tip.y, L0.tip.width, L0.tip.height, "codex");
    this.tipView.setDepth(2);
    scene.add.existing(this.tipView);
    this.filterBar = new SegmentedTabBar<CodexModuleFilterId>(scene, L0.filter.x, L0.filter.y, {
      style: SUB_TAB_BAR_STYLE,
      width: L0.filter.width,
      height: FILTER_BAR_HEIGHT,
      tabs: [
        { id: "all", label: t("codexFilter.all"), ...iconTab("icon_subtab_codex_all") },
        { id: "base", label: t("codexFilter.base"), ...iconTab("icon_subtab_codex_base") },
        { id: "ship", label: t("codexFilter.ship"), ...iconTab("icon_subtab_codex_ship") },
      ],
      activeId: "all",
      dir: this.dir,
    });
    this.filterBar.setDepth(3);
    this.filterBar.on(SEGMENTED_TAB_BAR_EVENT.TabChange, (id: CodexModuleFilterId) => this.selectModuleFilter(id));
    scene.add.existing(this.filterBar);
    this.synergyTipView = new TipCarouselView(
      scene,
      L0.tip.x,
      L0.tip.y,
      L0.tip.width,
      L0.tip.height,
      "codexSynergy",
    );
    this.synergyTipView.setDepth(2);
    scene.add.existing(this.synergyTipView);
    const cardH = computeCodexLayout(this.content, 1, this.contentBottom, this.dir, {
      inlineBannerEnabled: this.inlineBannerEnabled,
    }).contentHeight;
    this.scrollList = new ScrollableList(scene, L0.scroll.x, L0.scroll.y, {
      width: L0.scroll.width,
      height: L0.scroll.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
      itemMargin: cardH,
    });
    this.scrollList.setDepth(2);
    scene.add.existing(this.scrollList);
    const S0 = this.synergyViewport(L0);
    this.synergyScrollList = new ScrollableList(scene, S0.x, S0.y, {
      width: S0.width,
      height: S0.height,
      barWidth: 5,
      barColor: PALETTE_NUM.scrollBar,
      trackColor: PALETTE_NUM.scrollTrack,
      trackAlpha: 0.65,
      barAlpha: 0.95,
      scrollSpeed: 36,
      itemMargin: SYNERGY_ROW_H + SYNERGY_ROW_GAP,
    });
    this.synergyScrollList.setDepth(2);
    scene.add.existing(this.synergyScrollList);
    const M0 = this.synergyMultiplierRect(L0);
    this.synergyMultSelector = new MultiplierSelector(scene, M0.x, M0.y, {
      width: M0.width,
      height: SYNERGY_MULT_H,
      dir: this.dir,
      onChange: (mult) => this.onSynergyMultiplierChange?.(mult),
    });
    this.synergyMultSelector.setVisible(false);
    this.bannerSlot = new ScrollInlineBannerSlot(
      "codex",
      scene,
      this.scrollList,
      L0.bannerSlot,
      this.inlineBannerEnabled,
    );
    this.scrollList.on(SCROLLABLE_LIST_EVENT.Scroll, () => this.bannerSlot.sync(this.visible));
    this.subBar = new SegmentedTabBar<CodexSubTabId>(scene, x, panelBottom - SUB_BAR_HEIGHT, {
      style: SUB_TAB_BAR_STYLE,
      width,
      height: SUB_BAR_HEIGHT,
      tabs: [
        { id: "modules", label: t("subtab.codexModules"), ...iconTab("icon_subtab_codex_modules") },
        { id: "synergy", label: t("subtab.synergy"), ...iconTab("icon_subtab_codex_synergy") },
      ],
      activeId: "modules",
      dir: this.dir,
    });
    this.subBar.setDepth(3);
    this.subBar.on(SEGMENTED_TAB_BAR_EVENT.TabChange, (id: CodexSubTabId) => this.selectSub(id));
    scene.add.existing(this.subBar);
    this.setVisible(false);
  }

  update(
    entries: readonly CodexEntry[],
    synergies: readonly SynergyCodexRow[] = [],
    synergyUnlocked = true,
    synergyUnlockChapter = 1,
  ): void {
    this.lastEntries = entries;
    this.lastSynergies = synergies;
    this.synergyUnlocked = synergyUnlocked;
    this.synergyUnlockChapter = synergyUnlockChapter;
    this.applySynergyTabLock();
    this.ensureCards(this.filteredEntries().length);
    this.ensureSynergySection(synergies);
    this.renderCards();
    this.syncScrollContentHeight();
    this.renderSynergyRows();
    this.applySubVisibility();
  }

  private selectModuleFilter(id: CodexModuleFilterId): void {
    if (!this.moduleFilter.select(id)) return;
    this.filterBar.setActiveTab(id);
    const entries = this.filteredEntries();
    this.ensureCards(entries.length);
    this.renderCards();
    this.syncScrollContentHeight();
    this.applySubVisibility();
  }

  private selectSub(id: CodexSubTabId): void {
    if (id === "synergy" && !this.synergyUnlocked) {
      this.subBar.shakeTab("synergy", PALETTE_NUM.signalDanger);
      this.subBar.setActiveTab(this.selection.active);
      this.onSynergyLockedClick?.(this.synergyUnlockChapter);
      return;
    }
    if (!this.selection.select(id)) return;
    this.subBar.setActiveTab(id);
    this.applySubVisibility();
  }

  private applySubVisibility(): void {
    const showModules = this.visible && this.selection.active === "modules";
    const showSynergy = this.visible && this.selection.active === "synergy";
    this.tipView.setVisible(showModules);
    this.filterBar.setVisible(showModules);
    this.synergyTipView.setVisible(showSynergy);
    this.scrollList.setVisible(showModules);
    this.synergyScrollList.setVisible(showSynergy);
    this.synergyMultSelector.setVisible(showSynergy);
    this.setModuleObjectsVisible(showModules);
    this.setSynergyObjectsVisible(showSynergy);
    this.bannerSlot.sync(showModules);
  }

  private setModuleObjectsVisible(visible: boolean): void {
    const visibleCount = this.filteredEntries().length;
    for (let i = 0; i < this.cards.length; i++) {
      const card = this.cards[i]!;
      const show = visible && i < visibleCount;
      card.frame.setVisible(show);
      card.name.setVisible(show);
      card.desc.setVisible(show);
      card.moduleIcon.setVisible(show);
      card.lockIcon.setVisible(show);
      card.lockText.setVisible(show);
      card.newDot.setVisible(show);
    }
  }

  private setSynergyObjectsVisible(visible: boolean): void {
    for (let i = 0; i < this.synergyRows.length; i++) {
      const row = this.synergyRows[i]!;
      const show = visible && i < this.lastSynergies.length;
      row.frame.setVisible(show);
      row.synergyIcon.setVisible(show);
      row.name.setVisible(show);
      row.detail.setVisible(show);
      row.upgradeButton.setVisible(show);
    }
  }

  private applySynergyTabLock(): void {
    if (this.synergyUnlocked) {
      this.subBar.setTabLocked("synergy", false);
      return;
    }
    this.subBar.setTabLocked("synergy", true, {
      iconKey: LOCK_ICON_KEY,
      chapterLabel: String(this.synergyUnlockChapter),
      tint: PALETTE_NUM.signalDanger,
    });
    if (this.selection.active === "synergy") {
      this.selection.select("modules");
      this.subBar.setActiveTab("modules");
    }
  }

  private synergyLayout(count: number): {
    descY: number;
    descW: number;
    rows: { x: number; y: number; w: number; h: number }[];
    total: number;
  } {
    const L = computeCodexLayout(this.content, this.cards.length, this.contentBottom, this.dir, {
      inlineBannerEnabled: this.inlineBannerEnabled,
    });
    const x = SYNERGY_SIDE_INSET;
    const w = L.scroll.width - SYNERGY_SIDE_INSET - SYNERGY_SCROLLBAR_INSET;
    const descY = 0;
    const rowsTop = 0;
    const rows: { x: number; y: number; w: number; h: number }[] = [];
    for (let i = 0; i < count; i++) {
      rows.push({ x, y: rowsTop + i * (SYNERGY_ROW_H + SYNERGY_ROW_GAP), w, h: SYNERGY_ROW_H });
    }
    const total = count > 0 ? rowsTop + count * (SYNERGY_ROW_H + SYNERGY_ROW_GAP) : rowsTop;
    return { descY, descW: w, rows, total };
  }

  private synergyViewport(L: ReturnType<typeof computeCodexLayout>): Rect {
    const top = L.scroll.y;
    const bottom = L.scroll.y + L.scroll.height - SYNERGY_MULT_H - SYNERGY_MULT_GAP;
    return {
      x: L.scroll.x,
      y: top,
      width: L.scroll.width,
      height: Math.max(0, bottom - top),
    };
  }

  private synergyMultiplierRect(L: ReturnType<typeof computeCodexLayout>): Rect {
    const viewport = this.synergyViewport(L);
    return {
      x: viewport.x,
      y: viewport.y + viewport.height + SYNERGY_MULT_GAP,
      width: viewport.width,
      height: SYNERGY_MULT_H,
    };
  }

  private fitText(
    obj: Phaser.GameObjects.Text,
    value: string,
    role: CodexTextRole,
    wrapWidth: number,
  ): string {
    const spec = CODEX_TEXT_FIT[role];
    obj.setText(value).setWordWrapWidth(wrapWidth);
    for (let size = spec.preferred; size >= spec.min; size--) {
      obj.setFontSize(size);
      const wrapped = obj.getWrappedText(value);
      if (wrapped.length <= spec.maxLines) {
        return clampWrappedLines(wrapped, spec.maxLines);
      }
    }
    obj.setFontSize(spec.min);
    return clampWrappedLines(obj.getWrappedText(value), spec.maxLines);
  }

  private ensureSynergySection(rows: readonly SynergyCodexRow[]): void {
    if (rows.length === 0) return;
    const lay = this.synergyLayout(rows.length);
    while (this.synergyRows.length < rows.length) {
      const i = this.synergyRows.length;
      const r = lay.rows[i]!;
      const frame = this.scene.add
        .rectangle(0, 0, r.w, r.h, PALETTE_NUM.cellBg, 0.88)
        .setOrigin(0, 0)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlowSynergy, 0.3)
        .setDepth(1);
      const synergyIcon = this.scene.add
        .graphics()
        .setDepth(3)
        .setInteractive(new Phaser.Geom.Circle(0, 0, SYNERGY_BADGE_SIZE / 2 + 3), Phaser.Geom.Circle.Contains);
      synergyIcon.on("pointerup", () => {
        if (this.synergyScrollList.consumedDragGesture) return;
        const row = this.lastSynergies[i];
        if (!row) return;
        this.onSynergyInfoClick?.(row.id);
      });
      const name = this.scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textSecondary })
        .setOrigin(0, 0)
        .setDepth(3);
      const detail = this.scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textDim,
          wordWrap: {
            width:
              r.w -
              SYNERGY_ROW_PAD * 2 -
              SYNERGY_BADGE_SIZE -
              SYNERGY_BADGE_GAP -
              SYNERGY_BUTTON_W -
              SYNERGY_TEXT_GAP,
          },
        })
        .setOrigin(0, 0)
        .setDepth(3);
      const upgradeButton = new NineSliceButton(this.scene, 0, 0, {
        style: PRIMARY_BUTTON_STYLE,
        label: "",
        widthOverride: SYNERGY_BUTTON_W,
        heightOverride: SYNERGY_BUTTON_H,
        iconKey: REWARD_ICON_KEY.upgradeStone,
        iconSize: 12,
      });
      upgradeButton.setDepth(3);
      upgradeButton.on(NINE_SLICE_BUTTON_EVENT.Click, () => {
        if (this.synergyScrollList.consumedDragGesture) return;
        if (!this.synergyUnlocked) return;
        const row = this.lastSynergies[i];
        if (!row || !row.canUpgrade) return;
        this.onSynergyUpgrade?.(row.id);
      });
      this.synergyScrollList.addContent(frame, r.x, r.y);
      this.synergyScrollList.addContent(synergyIcon, r.x + SYNERGY_ROW_PAD + SYNERGY_BADGE_SIZE / 2, r.y + 21);
      this.synergyScrollList.addContent(
        name,
        r.x + SYNERGY_ROW_PAD + SYNERGY_BADGE_SIZE + SYNERGY_BADGE_GAP,
        r.y + 9,
      );
      this.synergyScrollList.addContent(
        detail,
        r.x + SYNERGY_ROW_PAD + SYNERGY_BADGE_SIZE + SYNERGY_BADGE_GAP,
        r.y + 30,
      );
      this.synergyScrollList.addContent(upgradeButton, r.x + r.w - SYNERGY_ROW_PAD - SYNERGY_BUTTON_W / 2, r.y + r.h / 2);
      this.synergyRows.push({ frame, synergyIcon, name, detail, upgradeButton });
    }
    this.synergyScrollList.setContentHeight(lay.total);
  }

  private renderSynergyRows(): void {
    this.lastSynergies.forEach((row, i) => {
      const o = this.synergyRows[i];
      if (!o) return;
      const lay = this.synergyLayout(this.lastSynergies.length).rows[i];
      const wrapW = lay
        ? lay.w -
          SYNERGY_ROW_PAD * 2 -
          SYNERGY_BADGE_SIZE -
          SYNERGY_BADGE_GAP -
          SYNERGY_BUTTON_W -
          SYNERGY_TEXT_GAP
        : 160;
      const nameWrapW = wrapW;
      const color = hexToNum(synergyGradeColor(row.grade));
      o.frame.setStrokeStyle(1, row.canUpgrade ? PALETTE_NUM.gridPathGlowSynergy : PALETTE_NUM.cellStroke, row.canUpgrade ? 0.75 : 0.3);
      drawSynergyIcon(o.synergyIcon, SYNERGY_BADGE_SIZE, color, PALETTE_NUM.cellBg, row.canUpgrade ? 1 : 0.78);
      o.name
        .setText(this.fitText(o.name, t("statCell.name", { name: row.name, level: row.level }), "synergyName", nameWrapW))
        .setColor(synergyGradeColor(row.grade));
      o.detail.setText(this.fitText(o.detail, row.detail, "synergyDetail", wrapW));
      o.detail.setColor(PALETTE.textDim);
      o.upgradeButton.setLabel(synergyBuyLabel(row));
      o.upgradeButton.setIconVisible(true);
      setButtonEnabled(o.upgradeButton, row.canUpgrade);
    });
  }

  private ensureCards(count: number): void {
    if (this.cards.length >= count) return;
    const L = computeCodexLayout(this.content, count, this.contentBottom, this.dir, {
      inlineBannerEnabled: this.inlineBannerEnabled,
    });
    this.poolMaxWidth = L.cards[0]?.nameWrapW;
    while (this.cards.length < count) {
      const i = this.cards.length;
      const cl = L.cards[i]!;
      const frame = this.scene.add
        .rectangle(0, 0, cl.frame.width, cl.frame.height, PALETTE_NUM.cellBg, 0.88)
        .setOrigin(0, 0)
        .setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.28)
        .setDepth(1)
        .setInteractive({ useHandCursor: true });
      frame.on("pointerup", () => {
        if (this.scrollList.consumedDragGesture) return;
        const e = this.filteredEntries()[i];
        if (!e) return;
        if (this.isCellLocked(e)) {
          this.playLockedFeedback(this.cards[i]!, e);
          return;
        }
        this.onRowClick?.(e.definitionId);
      });
      const name = this.scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.caption,
          color: PALETTE.textSecondary,
          align: "center",
          wordWrap: { width: cl.nameWrapW },
        })
        .setOrigin(0.5)
        .setDepth(3);
      const desc = this.scene.add
        .text(0, 0, "", {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.micro,
          color: PALETTE.textDim,
          align: "center",
          wordWrap: { width: cl.descWrapW },
        })
        .setOrigin(0.5, 0)
        .setDepth(3);
      const moduleIcon = this.scene.add
        .image(0, 0, "icon_tab_codex")
        .setDisplaySize(this.poolCellPx + 10, this.poolCellPx + 10)
        .setDepth(3);
      const lockIcon = this.scene.add
        .image(0, 0, LOCK_ICON_KEY)
        .setDisplaySize(this.poolCellPx, this.poolCellPx)
        .setTint(PALETTE_NUM.signalDanger)
        .setAlpha(0)
        .setDepth(4);
      const lockText = this.scene.add
        .text(0, 0, "", { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textBright })
        .setOrigin(0, 0.5)
        .setDepth(5);
      const newDot = this.scene.add
        .circle(0, 0, 5, PALETTE_NUM.signalDanger)
        .setAlpha(0)
        .setDepth(5);
      this.scrollList.addContent(frame, cl.frame.x, cl.frame.y);
      this.scrollList.addContent(moduleIcon, cl.cx, cl.poolY);
      this.scrollList.addContent(name, cl.cx, cl.nameY);
      this.scrollList.addContent(desc, cl.cx, cl.descY);
      this.scrollList.addContent(lockIcon, cl.cx - 9, cl.poolY);
      this.scrollList.addContent(lockText, cl.cx + 2, cl.poolY);
      this.scrollList.addContent(newDot, cl.frame.x + cl.frame.width - 8, cl.frame.y + 8);
      this.cards.push({ frame, name, desc, moduleIcon, lockIcon, lockText, newDot });
    }
    this.scrollList.setContentHeight(L.contentHeight);
  }

  private renderCards(): void {
    const entries = this.filteredEntries();
    entries.forEach((e, i) => {
      const card = this.cards[i];
      if (!card) return;
      const colorStr = e.discovered
        ? RARITY_COLOR[e.rarity as keyof typeof RARITY_COLOR] ?? PALETTE.textSecondary
        : LOCKED_COLOR;
      card.name.setText(this.fitText(card.name, this.entryName(e), "moduleName", this.poolMaxWidth ?? 128)).setColor(colorStr);
      card.desc.setText(
        this.fitText(card.desc, this.entryDesc(e), "moduleDesc", this.poolMaxWidth ?? 128),
      );
      card.desc.setColor(e.discovered ? PALETTE.textDim : LOCKED_COLOR);
      this.updateModuleIcon(card, e, e.discovered ? hexToNum(colorStr) : PALETTE_NUM.portOff);
      this.updateLockBadge(card, e);
      card.newDot.setAlpha(e.newlyUnlocked ? 1 : 0);
    });
    for (let i = entries.length; i < this.cards.length; i++) {
      const card = this.cards[i]!;
      card.frame.setVisible(false);
      card.name.setVisible(false);
      card.desc.setVisible(false);
      card.moduleIcon.setVisible(false);
      card.lockIcon.setVisible(false);
      card.lockText.setVisible(false);
      card.newDot.setVisible(false);
    }
  }

  /**
   * 파츠 단위 잠금 배지 갱신 — 아직 해금된 조각이 하나도 없으면 중앙에 자물쇠 alpha 1 + 첫 해금 챕터를 표시한다.
   * 조각이 1개 이상 열리면 미발견이어도 상세 팝업/선강화 진입을 허용한다.
   */
  private updateLockBadge(card: CodexCard, entry: CodexEntry): void {
    if (!this.isCellLocked(entry)) {
      card.lockIcon.setAlpha(0);
      card.lockText.setText("");
      return;
    }
    card.lockIcon.setAlpha(1);
    card.lockText.setText(this.fitText(card.lockText, String(this.cellUnlockChapter(entry)), "lockBadge", 32));
  }

  /** 잠긴 셀 판정(클릭 차단용) — 해금된 조각이 0개일 때만 정보 팝업을 차단한다. */
  private isCellLocked(entry: CodexEntry): boolean {
    return !entry.piecePool.some((p) => isPieceUnlocked(p, entry.bestChapter));
  }

  /** 셀 해금 챕터 = 첫 조각이 풀리는 최소 unlockChapter. 빈 풀은 1(Math.min 0인자=Infinity 방어). */
  private cellUnlockChapter(entry: CodexEntry): number {
    const first = Math.min(...entry.piecePool.map((p) => p.unlockChapter ?? 1));
    return Number.isFinite(first) ? Math.max(1, first) : 1;
  }

  /** 잠긴 셀 클릭 피드백 — 빨간 테두리 + 좌우 흔들기(±5px) 후 토스트 안내(onLockedClick). 완료 시 원래 테두리 복원. */
  private playLockedFeedback(card: CodexCard, entry: CodexEntry): void {
    card.frame.setStrokeStyle(2, PALETTE_NUM.signalDanger, 1);
    const baseX = card.frame.x;
    this.scene.tweens.add({
      targets: card.frame,
      x: { from: baseX - 5, to: baseX + 5 },
      duration: 45,
      yoyo: true,
      repeat: 2,
      ease: "Sine.inOut",
      onComplete: () => {
        card.frame.setX(baseX).setStrokeStyle(1, PALETTE_NUM.gridPathGlow, 0.28);
      },
    });
    this.onLockedClick?.(this.cellUnlockChapter(entry));
  }

  /** 도감 셀 대표 아이콘 — 그리드 타일과 같은 기능 아이콘을 쓴다. */
  private updateModuleIcon(card: CodexCard, entry: CodexEntry, color: number): void {
    if (this.isCellLocked(entry)) {
      card.moduleIcon.setAlpha(0);
      return;
    }
    card.moduleIcon
      .setTexture(entry.iconKey ?? moduleCodexIconKey(entry.definitionId))
      .setTint(color)
      .setAlpha(1);
  }

  private isShipLauncher(entry: CodexEntry): boolean {
    return (
      entry.kind === "shipLauncher" ||
      entry.spaceshipId !== undefined ||
      entry.definitionId === "launcher" ||
      entry.definitionId.startsWith(SPACESHIP_LAUNCHER_CODEX_PREFIX)
    );
  }

  /** 필터 변경 후 scroll contentHeight를 필터된 카드 수 기준으로 갱신 — virtualizer가 숨긴 카드를 재표시하는 것을 방지. */
  private syncScrollContentHeight(): void {
    const count = this.filteredEntries().length;
    const L = computeCodexLayout(this.content, count, this.contentBottom, this.dir, {
      inlineBannerEnabled: this.inlineBannerEnabled,
    });
    this.scrollList.setContentHeight(L.contentHeight);
  }

  private filteredEntries(): readonly CodexEntry[] {
    if (this.moduleFilter.active === "base") return this.lastEntries.filter((entry) => !this.isShipLauncher(entry));
    if (this.moduleFilter.active === "ship") return this.lastEntries.filter((entry) => this.isShipLauncher(entry));
    return this.lastEntries;
  }

  private entryName(entry: CodexEntry): string {
    return entry.nameKey ? t(entry.nameKey) : t(`module.${entry.definitionId}.name`);
  }

  private entryDesc(entry: CodexEntry): string {
    return entry.descKey ? t(entry.descKey) : t(`module.${entry.definitionId}.codexDesc`);
  }

  /** 언어 변경 — 헤더 + 카드 텍스트를 재설정(객체 재사용, 재생성 0). */
  applyLanguage(): void {
    this.subBar.setTabLabels((id) => (id === "modules" ? t("subtab.codexModules") : t("subtab.synergy")));
    this.filterBar.setTabLabels((id) => t(`codexFilter.${id}`));
    this.tipView.applyLanguage();
    this.synergyTipView.applyLanguage();
    this.synergyMultSelector.applyLanguage();
    this.renderCards();
    this.renderSynergyRows();
  }

  /** 방향(LTR↔RTL) 전환 — 헤더 정렬 + 카드 그리드 col 미러 재배치(카드 내부는 cx 중앙정렬이라 자동). */
  relayout(dir: TextDirection): void {
    this.dir = dir;
    const L = computeCodexLayout(this.content, this.cards.length, this.contentBottom, dir, {
      inlineBannerEnabled: this.inlineBannerEnabled,
    });
    this.header.setPosition(L.header.x, L.header.y).setOrigin(L.textOriginX, 0);
    this.subBar.relayout(dir);
    this.filterBar.setPosition(L.filter.x, L.filter.y);
    this.filterBar.relayout(dir);
    const M = this.synergyMultiplierRect(L);
    this.synergyMultSelector.setPosition(M.x, M.y);
    this.synergyMultSelector.relayout(dir);
    this.tipView.setPosition(L.tip.x, L.tip.y);
    this.tipView.relayout(dir);
    this.synergyTipView.setPosition(L.tip.x, L.tip.y);
    this.synergyTipView.relayout(dir);
    const modulePositions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
    this.cards.forEach((card, i) => {
      const cl = L.cards[i];
      if (!cl) return;
      modulePositions.push(
        { obj: card.frame, lx: cl.frame.x, ly: cl.frame.y },
        { obj: card.moduleIcon, lx: cl.cx, ly: cl.poolY },
        { obj: card.name, lx: cl.cx, ly: cl.nameY },
        { obj: card.desc, lx: cl.cx, ly: cl.descY },
        { obj: card.lockIcon, lx: cl.cx - 9, ly: cl.poolY },
        { obj: card.lockText, lx: cl.cx + 2, ly: cl.poolY },
        { obj: card.newDot, lx: cl.frame.x + cl.frame.width - 8, ly: cl.frame.y + 8 },
      );
    });
    this.bannerSlot.pushRelayout(modulePositions, L.bannerSlot);
    this.scrollList.relayoutContent(modulePositions);
    if (this.synergyRows.length > 0) {
      const synergyPositions: { obj: Phaser.GameObjects.GameObject; lx: number; ly: number }[] = [];
      const lay = this.synergyLayout(this.synergyRows.length);
      this.synergyRows.forEach((o, i) => {
        const r = lay.rows[i];
        if (!r) return;
        o.frame.setSize(r.w, r.h);
        o.detail.setWordWrapWidth(
          r.w -
            SYNERGY_ROW_PAD * 2 -
            SYNERGY_BADGE_SIZE -
            SYNERGY_BADGE_GAP -
            SYNERGY_BUTTON_W -
            SYNERGY_TEXT_GAP,
        );
        synergyPositions.push(
          { obj: o.frame, lx: r.x, ly: r.y },
          { obj: o.synergyIcon, lx: r.x + SYNERGY_ROW_PAD + SYNERGY_BADGE_SIZE / 2, ly: r.y + 21 },
          {
            obj: o.name,
            lx: r.x + SYNERGY_ROW_PAD + SYNERGY_BADGE_SIZE + SYNERGY_BADGE_GAP,
            ly: r.y + 9,
          },
          {
            obj: o.detail,
            lx: r.x + SYNERGY_ROW_PAD + SYNERGY_BADGE_SIZE + SYNERGY_BADGE_GAP,
            ly: r.y + 30,
          },
          { obj: o.upgradeButton, lx: r.x + r.w - SYNERGY_ROW_PAD - SYNERGY_BUTTON_W / 2, ly: r.y + r.h / 2 },
        );
      });
      this.synergyScrollList.relayoutContent(synergyPositions);
      this.synergyScrollList.setContentHeight(lay.total);
    }
    this.applySubVisibility();
  }

  /** 셸 펼치기/접기 — 콘텐츠 영역(상단) 갱신 후 relayout으로 헤더/스크롤 뷰포트/카드 그리드 재배치(카드 더 보임). */
  resize(rect: TabContentRect): void {
    this.content = { x: rect.x, y: rect.y, width: rect.width, height: 0 };
    const L = computeCodexLayout(this.content, this.cards.length, this.contentBottom, this.dir, {
      inlineBannerEnabled: this.inlineBannerEnabled,
    });
    this.scrollList.resize(L.scroll.x, L.scroll.y, L.scroll.width, L.scroll.height);
    const S = this.synergyViewport(L);
    this.synergyScrollList.resize(S.x, S.y, S.width, S.height);
    this.relayout(this.dir);
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    // 상단 도감 제목은 서브탭과 중복되어 표시하지 않는다.
    this.header.setVisible(false);
    this.subBar.setVisible(visible);
    this.applySubVisibility();
  }

  currentSynergyMultiplier(): Multiplier {
    return this.synergyMultSelector.current();
  }
}
